// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-84 round 2 — the Enrollment queue's chips and stage words. The SERVER decides each row's stage and
// bucket (one stage rule, shared with the Clinical lists); this file only names them and counts chips.
import type { CareRxEnrollmentBucket, CareRxEnrollmentQueueRow, CareRxEnrollmentStage } from '../../api/client'

export type EnrollChip = Exclude<CareRxEnrollmentBucket, 'other'> | 'all'

/** Chip order (board Q3): Needs review · Pending labs · Clinical · Approved · Assigned · Active · All. */
export const ENROLL_CHIPS: readonly (readonly [EnrollChip, string])[] = [
  ['review', 'Needs review'], ['labs', 'Pending labs'], ['clinical', 'Clinical'],
  ['approved', 'Approved'], ['assigned', 'Assigned'], ['active', 'Active'], ['all', 'All'],
]

/** What a chip holds when its name alone does not say (board R2: "Clinical" groups three lists). Shown as the
 *  chip's tooltip and, when the chip is selected, as a caption under the chips (no hover on touch screens). */
export const CHIP_HINT: Partial<Record<EnrollChip, string>> = {
  clinical: 'On hold · Renewal · Pending clinical review',
}

const STAGE_LABEL: Record<CareRxEnrollmentStage, string> = {
  on_hold: 'On hold',
  renewal: 'Renewal',
  pending_labs: 'Pending labs',
  pending_review: 'Pending clinical review',
  pending_review_callback: 'Pending clinical review · call back',
  approved: 'Approved',
}

/** The words the Status cell shows: the stage when there is one, else the stored enrollment status. */
export function stageText(row: Pick<CareRxEnrollmentQueueRow, 'stage' | 'enrollment_status'>): string {
  if (row.stage && STAGE_LABEL[row.stage]) return STAGE_LABEL[row.stage]
  return row.enrollment_status ? row.enrollment_status.replace(/_/g, ' ') : '—'
}

/** The Status cell's tooltip: the stored enrollment record is still there, one hover away. */
export const enrollmentRecordTip = (row: Pick<CareRxEnrollmentQueueRow, 'enrollment_status'>) =>
  `Enrollment record: ${row.enrollment_status ? row.enrollment_status.replace(/_/g, ' ') : '—'}`

export const inChip = (chip: EnrollChip, row: Pick<CareRxEnrollmentQueueRow, 'bucket'>) =>
  chip === 'all' || row.bucket === chip

/** Chip count from the server's whole-queue bucket counts; "All" is their sum (it includes "other"). */
export function chipCount(chip: EnrollChip, counts: Partial<Record<CareRxEnrollmentBucket, number>> | undefined,
  rows: readonly Pick<CareRxEnrollmentQueueRow, 'bucket'>[]): number {
  if (!counts) return rows.filter(r => inChip(chip, r)).length
  if (chip === 'all') return Object.values(counts).reduce<number>((n, c) => n + (c ?? 0), 0)
  return counts[chip] ?? 0
}

/** Owner UI surf 2026-08-13: never land on an empty "Needs review" when other chips have rows. */
export function landingChip(counts: Partial<Record<CareRxEnrollmentBucket, number>> | undefined,
  rows: readonly Pick<CareRxEnrollmentQueueRow, 'bucket'>[]): EnrollChip {
  for (const [chip] of ENROLL_CHIPS) if (chip !== 'all' && chipCount(chip, counts, rows) > 0) return chip
  return chipCount('all', counts, rows) > 0 ? 'all' : 'review'
}

/** The row action that runs the real workflow step for this stage (board Q5, option c), if any. */
export function stageAction(row: Pick<CareRxEnrollmentQueueRow, 'stage' | 'therapy_id'>): 'send_to_labs' | 'recheck_labs' | null {
  if (!row.therapy_id) return null
  if (row.stage === 'pending_review' || row.stage === 'pending_review_callback') return 'send_to_labs'
  if (row.stage === 'pending_labs') return 'recheck_labs'
  return null
}
